import { useCallback, useEffect, useRef, useState } from "react";
import type { MascotMood } from "./types";
import { MASCOT_PULSE_MS } from "./types";

export interface UseMascotMoodOptions {
    /** Derived base mood (idle / nervous). Defaults to "idle". */
    baseMood?: MascotMood;
}

export interface UseMascotMoodResult {
    mood: MascotMood;
    /** Set a transient mood that auto-reverts to base after ms (defaults per mood). */
    pulse: (mood: MascotMood, ms?: number) => void;
    /** Immediately revert to base mood. */
    reset: () => void;
}

/**
 * Mascot mood state machine.
 *
 * `baseMood` is the resting state (e.g. "idle" or "nervous"). `pulse()` sets a
 * transient celebratory/reaction mood that auto-reverts to the base after a
 * timeout. If `baseMood` changes mid-pulse, the pulse keeps its timeout and
 * then reverts to the *latest* base.
 */
export const useMascotMood = (opts?: UseMascotMoodOptions): UseMascotMoodResult => {
    const baseMood = opts?.baseMood ?? "idle";
    const [mood, setMood] = useState<MascotMood>(baseMood);
    const baseRef = useRef(baseMood);
    const pulseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const hasActivePulseRef = useRef(false);

    // Track the latest base mood without interrupting an active pulse.
    useEffect(() => {
        baseRef.current = baseMood;
        if (!hasActivePulseRef.current) {
            setMood(baseMood);
        }
    }, [baseMood]);

    const clearPulseTimer = useCallback(() => {
        if (pulseTimerRef.current !== null) {
            clearTimeout(pulseTimerRef.current);
            pulseTimerRef.current = null;
        }
    }, []);

    const pulse = useCallback((nextMood: MascotMood, ms?: number) => {
        clearPulseTimer();
        hasActivePulseRef.current = true;
        setMood(nextMood);
        const duration = ms ?? MASCOT_PULSE_MS[nextMood as keyof typeof MASCOT_PULSE_MS] ?? 2000;
        pulseTimerRef.current = setTimeout(() => {
            hasActivePulseRef.current = false;
            setMood(baseRef.current);
            pulseTimerRef.current = null;
        }, duration);
    }, [clearPulseTimer]);

    const reset = useCallback(() => {
        clearPulseTimer();
        hasActivePulseRef.current = false;
        setMood(baseRef.current);
    }, [clearPulseTimer]);

    // Cleanup on unmount.
    useEffect(() => {
        return () => {
            if (pulseTimerRef.current !== null) {
                clearTimeout(pulseTimerRef.current);
                pulseTimerRef.current = null;
            }
        };
    }, []);

    return { mood, pulse, reset };
};